import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import {
  useCalculator, PopularChips, VehicleCards, TrustBadges,
  PriceResult, AZFilter, DestinationDropdown, CTAButtons,
} from './CalculatorShared';

interface Props { onInteraction: () => void; onConversion: () => void; }

const VariantA = ({ onInteraction, onConversion }: Props) => {
  const { t } = useLanguage();
  const calc = useCalculator(onInteraction);

  return (
    <section id="calculator" className="py-20 md:py-28 bg-gradient-to-b from-muted/50 to-background" data-ab-variant="A">
      <div className="container">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{t.calculator.title}</h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">{t.calculator.subtitle}</p>
          <div className="w-16 h-1 bg-gradient-gold mx-auto mt-6 rounded-full" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-card rounded-2xl shadow-card border border-border p-6 md:p-8"
        >
          {/* Popular chips */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t.calculator.popularDestinations}</p>
            <PopularChips onChipClick={calc.handleChipClick} />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: A-Z + Destination */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t.calculator.azFilter}</label>
                <AZFilter activeLetter={calc.activeLetter} availableLetters={calc.availableLetters} onLetterClick={calc.handleLetterClick} />
              </div>
              <DestinationDropdown
                isOpen={calc.isOpen} selected={calc.selected} grouped={calc.grouped}
                dropdownRef={calc.dropdownRef as React.RefObject<HTMLDivElement>}
                inputRef={calc.inputRef as React.RefObject<HTMLInputElement>}
                query={calc.query} setQuery={calc.setQuery} setSelected={calc.setSelected as any}
                setIsOpen={calc.setIsOpen} setActiveLetter={calc.setActiveLetter}
                handleSelect={calc.handleSelect} handleClear={calc.handleClear}
                onInteraction={onInteraction}
              />
            </div>

            {/* Right: Vehicle + Price */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t.calculator.vehicleType}</label>
                <VehicleCards vehicleType={calc.vehicleType} setVehicleType={calc.setVehicleType} />
              </div>

              {calc.selected && calc.selected.km > 0 && (
                <PriceResult
                  selected={calc.selected}
                  finalPrice={calc.finalPrice}
                  basePrice={calc.basePrice}
                  multiplier={calc.multiplier}
                  km={calc.selected.km}
                />
              )}

              <div onClick={onConversion}>
                <CTAButtons />
              </div>
            </div>
          </div>

          <TrustBadges className="mt-6 pt-4 border-t border-border" />
        </motion.div>
      </div>
    </section>
  );
};

export default VariantA;
